/**
 * Organisations API Queries
 *
 * Query keys and fetch operations.
 */

// API client
import { apiClient } from '../client';
import { createResourceListQueryKeys } from '../resourceListPage/queryKeys';

// Types
import type {
  FetchOrganisationsParams,
  OrganisationSearchResult,
  OrganisationsResponse,
  OrganisationShowResponse,
} from './types';

export const organisationsQueryKeys = {
  ...createResourceListQueryKeys<FetchOrganisationsParams>('organisations'),
  // Combobox search surface — not part of the shared list-page key set.
  search: (query: string) => ['organisations', 'search', query] as const,
};

// Combobox search surface — existing org pickers in the order + contact flows
// (useSearchOrganisations) rely on this; keep it alongside the list queries.
export async function searchOrganisations(query: string): Promise<OrganisationSearchResult[]> {
  const response = await apiClient.get<{ organisations: OrganisationSearchResult[] }>(
    '/api/organisations/search',
    // Scope to name only (GRAIN-2556): the combobox renders just the name, so
    // website/email hits would look unexplained. See GRAIN-2530 search_field.
    { params: { q: query, search_field: 'name' } },
  );
  return response.data.organisations;
}

export async function getOrganisations(
  params: FetchOrganisationsParams = {},
): Promise<OrganisationsResponse> {
  const { data } = await apiClient.get<OrganisationsResponse>('/api/organisations', { params });
  return data;
}

export async function getOrganisation(id: number): Promise<OrganisationShowResponse> {
  const { data } = await apiClient.get<OrganisationShowResponse>(`/api/organisations/${id}`);
  return data;
}
